import type React from 'react';

import {
  Button,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import { Modals } from 'components';
import {
  BarGraph,
  CardContainer,
  CardHeader,
  EmptyContainer,
  HistoryGraph,
} from './styles';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import KpiCard from 'components/KpiCard';
import InsightsIcon from '@mui/icons-material/Insights';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import PlaylistRemoveIcon from '@mui/icons-material/PlaylistRemove';
import ChecklistIcon from '@mui/icons-material/Checklist';
import { useFontMonitoring } from 'contexts/fontMonitoring';
import { useEffect, useMemo, useState } from 'react';
import type {
  IFontReportViewModel,
  IFontStatusHistoryViewModel,
  IFontTestViewModel,
  IHistoryTestDataViewModel,
} from 'viewModels';
import { DatePicker, Empty, Popconfirm, type TimeRangePickerProps } from 'antd';
import { FontMonitoringController } from 'controllers/FontMonitoringController';
import moment from 'moment';
import {
  Download,
  KeyboardDoubleArrowDown,
  Visibility,
} from '@mui/icons-material';
import { FontStatus, FontTestStatus } from 'types/enums';
import { Bar, DualAxes } from '@ant-design/plots';
import dayjs from 'dayjs';
import { useAlert } from 'contexts';
import { formatExecutionTime } from 'utils/timeHelpers';
const { RangePicker } = DatePicker;

// Componente para a aba de Relatórios
const FontReportsTab: React.FC = () => {
  const alert = useAlert();
  const theme = useTheme();
  const [currentReport, setCurrentReport] = useState<IFontTestViewModel>();
  const [showSummaryTest, setShowSummaryTest] = useState<boolean>(false);
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const [isLoadingReport, setIsLoadingReport] = useState<boolean>(false);
  const [openModalDetais, setOpenModalDetais] = useState(false);
  const [selectedFont, setSelectedFont] =
    useState<IFontStatusHistoryViewModel | null>(null);
  const {
    reports,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    handleFetchNextPage,
    testHistory,
    isLoadingTestHistory,
    fontStatusHistory,
    isLoadingFontsHistory,
    startHistoryDate,
    endHistoryDate,
    setEndHistoryDate,
    setStartHistoryDate,
    startFontDate,
    endFontDate,
    setEndFontDate,
    setStartFontDate,
    handleCancelReport,
  } = useFontMonitoring();
  const [lastCompletedReport, setLastCompletedReport] =
    useState<IFontTestViewModel | null>(null);

  useEffect(() => {
    if (!reports || reports.length === 0) {
      setLastCompletedReport(null);
      return;
    }

    const completedReport = reports
      .filter(
        (report: IFontTestViewModel) =>
          report.status === FontTestStatus.COMPLETED && report.createdAt
      )
      .sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      )
      .at(0);

    setLastCompletedReport(completedReport || null);
  }, [reports]);

  const trueStatusCount = useMemo(() => {
    return (
      lastCompletedReport?.fonts?.filter(
        (font: IFontReportViewModel) => font.status === FontStatus.AVAILABLE
      ).length || 0
    );
  }, [lastCompletedReport]);

  const falseStatusCount = useMemo(() => {
    return (
      lastCompletedReport?.fonts?.filter(
        (font: IFontReportViewModel) => font.status === FontStatus.UNAVAILABLE
      ).length || 0
    );
  }, [lastCompletedReport]);

  const statusLabel = (status: FontTestStatus) => {
    switch (status) {
      case FontTestStatus.FAILED:
        return 'Falha';
      case FontTestStatus.PENDING:
        return 'Pendente';
      case FontTestStatus.PROCESSING:
        return 'Processando';
      case FontTestStatus.COMPLETED:
        return 'Finalizado';
      case FontTestStatus.CANCELED:
        return 'Cancelado';
      default:
        return 'Falha';
    }
  };

  const handleTriggerReport = () => {
    setIsDisabled(true);
    const processingReport = reports.find(
      report => report.status === FontTestStatus.PROCESSING
    );
    if (processingReport) {
      alert.show({
        type: 'alert',
        title: `Erro`,
        timeout: 2000,
        description:
          'Já existe um relatório em processamento, aguarde sua finalização.',
      });
    } else {
      FontMonitoringController.triggerReport();
    }
    setTimeout(() => {
      setIsDisabled(false);
    }, 10000);
  };

  const formatTestHistoryDates = (data: IHistoryTestDataViewModel[]) => {
    return testHistory.map(item => ({
      Data: dayjs(item.date).toDate(),
      Disponíveis: item.availableCount,
      Indisponíveis: item.unavailableCount,
    }));
  };

  const handleFontClick = (event: any) => {
    if (event.data) {
      const { data } = event.data;
      const font = fontStatusHistory.find(f => f.fontKey === data.Key);
      if (font) {
        setSelectedFont(font);
        setOpenModalDetais(true);
      }
    }
  };

  const convertToBarChartData = (data: IFontStatusHistoryViewModel[]) => {
    return data.map(font => {
      const availableCount = font.statusHistory.filter(
        status => status.status === FontStatus.AVAILABLE
      ).length;
      const totalCount = font.statusHistory.length;
      const successPercentage = availableCount / totalCount;
      return {
        Nome: font.fontName,
        Rate: successPercentage,
        Key: font.fontKey,
        successCount: availableCount,
      };
    });
  };

  const handleExportReports = () => {
    setIsLoadingReport(true);
    FontMonitoringController.exportReports().finally(() =>
      setIsLoadingReport(false)
    );
  };

  const onChangeTestHistoryDate = (value: any) => {
    if (Array.isArray(value)) {
      const [start, end] = value;
      if (start && end) {
        setStartHistoryDate(start);
        setEndHistoryDate(end.endOf('day'));
      }
    } else {
      if (value) {
        setStartHistoryDate(value);
      } else {
        setStartHistoryDate(dayjs());
      }
    }
  };

  const onChangeFontsHistoryDate = (value: any) => {
    if (Array.isArray(value)) {
      const [start, end] = value;
      if (start && end) {
        setStartFontDate(start);
        setEndFontDate(end.endOf('day'));
      }
    } else {
      if (value) {
        setStartFontDate(value);
      } else {
        setStartFontDate(dayjs());
      }
    }
  };

  const config = {
    data: formatTestHistoryDates(testHistory),
    xField: 'Data',
    responsive: true,
    autoFit: true,
    children: [
      {
        type: 'area',
        yField: 'Disponíveis',
        shapeField: 'smooth',
        style: {
          fill: 'rgba(91, 249, 117, 0.3)',
          stroke: '#5AD8A6',
          lineWidth: 2,
        },
        axis: {
          y: {
            position: 'left',
            title: 'Disponíveis',
            style: { titleFill: '#5AD8A6' },
          },
        },
      },
      {
        type: 'area',
        yField: 'Indisponíveis',
        shapeField: 'smooth',
        style: {
          fill: 'rgba(237, 21, 21, 0.3)',
          stroke: '#db0f0f',
          lineWidth: 2,
        },
        axis: {
          y: {
            position: 'right',
            title: 'Indisponíveis',
            style: { titleFill: '#db0f0f' },
          },
        },
      },
    ],
  };

  const barConfig = {
    data: convertToBarChartData(fontStatusHistory),
    xField: 'Nome',
    yField: 'Rate',
    sort: { reverse: true },
    label: {
      text: 'Rate',
      formatter: '.1%',
      style: {
        textAlign: (d: any) => (+d.Rate > 0.008 ? 'right' : 'start'),
        fill: (d: any) => (+d.Rate > 0.008 ? '#fff' : '#000'),
        dx: (d: any) => (+d.Rate > 0.008 ? -5 : 5),
      },
    },
    xAxis: {
      label: {
        rotate: -45,
        style: { textAlign: 'center' },
      },
    },
    meta: {
      Nome: { alias: 'Fonte' },
      Rate: { alias: 'Taxa de Sucesso (%)', min: 0, max: 100 },
    },
    responsive: true,
    autoFit: true,
    interactions: [{ type: 'element-active' }],
    onReady: (plot: any) => {
      plot.on('click', handleFontClick);
    },
  };

  // Presets para exibir no seletor de Data
  const rangePresets: TimeRangePickerProps['presets'] = [
    {
      label: 'Últimos 7 Dias',
      value: [dayjs().add(-7, 'd'), dayjs().endOf('day')],
    },
    {
      label: 'Últimos 14 Dias',
      value: [dayjs().add(-14, 'd'), dayjs().endOf('day')],
    },
    {
      label: 'Último mês',
      value: [dayjs().add(-1, 'M'), dayjs().endOf('day')],
    },
    {
      label: 'Últimos 3 meses',
      value: [dayjs().add(-3, 'M'), dayjs().endOf('day')],
    },
    {
      label: 'Últimos 6 meses',
      value: [dayjs().add(-6, 'M'), dayjs().endOf('day')],
    },
  ];

  return (
    <>
      <Paper
        sx={{
          p: 4,
          mb: 3,
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
        }}
      >
        <Typography
          component="p"
          variant="h4"
          color="primary"
          sx={{
            mb: 3,
            fontWeight: 600,
            position: 'relative',
            '&:after': {
              content: '""',
              position: 'absolute',
              bottom: -8,
              left: 0,
              width: 60,
              height: 3,
              backgroundColor: theme.palette.primary.main,
              borderRadius: 4,
            },
          }}
        >
          Insights do relatório mais recente
        </Typography>
        {isLoading ? (
          <CardContainer>
            <div className="cards">
              {Array.from({ length: 4 }).map((_, index) => (
                <Skeleton
                  key={index}
                  sx={{
                    height: 120,
                    minWidth: '200px',
                    maxWidth: '300px',
                    margin: '10px',
                    flex: '1 1 calc(20% - 40px)',
                    borderRadius: '8px',
                  }}
                  animation="wave"
                  variant="rectangular"
                />
              ))}
            </div>
            <div className="buttons">
              <Button
                disabled
                className="start-report"
                variant="contained"
                color="primary"
                sx={{
                  mt: 2,
                  padding: '10px 24px',
                  textTransform: 'none',
                  fontWeight: 500,
                }}
              >
                Iniciar varredura
              </Button>
            </div>
          </CardContainer>
        ) : (
          <>
            {reports && reports.length > 0 ? (
              <CardContainer>
                <div className="cards">
                  <KpiCard
                    title="Fontes analisadas"
                    value={lastCompletedReport?.fonts?.length ?? 0}
                    icon={<FormatListBulletedIcon />}
                  />
                  <KpiCard
                    title="Fontes Funcionais"
                    value={trueStatusCount}
                    icon={<ChecklistIcon />}
                    backgroundColor="#167a12"
                  />
                  <KpiCard
                    title="Fontes Inativas"
                    value={falseStatusCount}
                    icon={<PlaylistRemoveIcon />}
                    backgroundColor="#af1a3a"
                  />
                  <KpiCard
                    title="Percentual de Fontes Ativas"
                    value={
                      lastCompletedReport?.fonts?.length
                        ? (trueStatusCount / lastCompletedReport.fonts.length) *
                          100
                        : 0
                    }
                    percent
                    icon={<InsightsIcon />}
                    backgroundColor="#107066"
                  />
                </div>
                <div className="buttons">
                  <Button
                    disabled={isDisabled}
                    className="start-report"
                    variant="contained"
                    color="primary"
                    onClick={handleTriggerReport}
                    sx={{
                      mt: 2,
                      padding: '10px 24px',
                      textTransform: 'none',
                      fontWeight: 500,
                      boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
                    }}
                  >
                    Iniciar verificação
                  </Button>
                </div>
              </CardContainer>
            ) : (
              <EmptyContainer>
                <Empty
                  description={
                    'Nenhum relatório encontrado, crie um para exibir os insights mais recentes'
                  }
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
                <Button
                  disabled={isDisabled}
                  className="start-report"
                  variant="contained"
                  color="primary"
                  onClick={handleTriggerReport}
                  sx={{
                    mt: 2,
                    padding: '10px 24px',
                    textTransform: 'none',
                    fontWeight: 500,
                    boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
                  }}
                >
                  Iniciar verificação
                </Button>
              </EmptyContainer>
            )}
          </>
        )}
      </Paper>
      <Paper
        sx={{
          p: 4,
          mb: 3,
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
        }}
      >
        <CardHeader>
          <Typography
            component="p"
            variant="h4"
            color="primary"
            sx={{
              fontWeight: 600,
              position: 'relative',
              '&:after': {
                content: '""',
                position: 'absolute',
                bottom: -8,
                left: 0,
                width: 60,
                height: 3,
                backgroundColor: theme.palette.primary.main,
                borderRadius: 4,
              },
            }}
          >
            Disponibilidade de fontes
          </Typography>
          <RangePicker
            format={'DD/MM/YYYY'}
            value={[startHistoryDate, endHistoryDate]}
            presets={rangePresets}
            maxDate={dayjs()}
            onChange={onChangeTestHistoryDate}
            style={{ borderRadius: '8px' }}
          />
        </CardHeader>
        {!isLoadingTestHistory ? (
          testHistory.length > 0 ? (
            <HistoryGraph>
              <DualAxes {...config} />
            </HistoryGraph>
          ) : (
            <Empty
              description={
                'Nenhum relatório encontrado, crie um para exibir a disponibilidade de Fontes'
              }
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          )
        ) : (
          <Skeleton
            sx={{ height: 300, borderRadius: '8px' }}
            animation="wave"
            variant="rectangular"
          />
        )}
      </Paper>
      <Paper
        sx={{
          p: 4,
          mb: 3,
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
        }}
      >
        <CardHeader>
          <Typography
            component="p"
            variant="h4"
            color="primary"
            sx={{
              fontWeight: 600,
              position: 'relative',
              '&:after': {
                content: '""',
                position: 'absolute',
                bottom: -8,
                left: 0,
                width: 60,
                height: 3,
                backgroundColor: theme.palette.primary.main,
                borderRadius: 4,
              },
            }}
          >
            Taxa de sucesso
          </Typography>
          <RangePicker
            format={'DD/MM/YYYY'}
            value={[startFontDate, endFontDate]}
            presets={rangePresets}
            maxDate={dayjs()}
            onChange={onChangeFontsHistoryDate}
            style={{ borderRadius: '8px' }}
          />
        </CardHeader>
        {!isLoadingFontsHistory ? (
          fontStatusHistory.length > 0 ? (
            <BarGraph>
              <Bar {...barConfig} />
            </BarGraph>
          ) : (
            <Empty
              description={
                'Nenhum relatório encontrado, crie um para exibir a Taxa de sucesso das Fontes.'
              }
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          )
        ) : (
          <Skeleton
            sx={{ height: 2000, borderRadius: '8px' }}
            animation="wave"
            variant="rectangular"
          />
        )}
      </Paper>
      <Paper
        sx={{
          p: 4,
          mb: 3,
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
        }}
      >
        <CardHeader>
          <Typography
            component="p"
            variant="h4"
            color="primary"
            sx={{
              fontWeight: 600,
              position: 'relative',
              '&:after': {
                content: '""',
                position: 'absolute',
                bottom: -8,
                left: 0,
                width: 60,
                height: 3,
                backgroundColor: theme.palette.primary.main,
                borderRadius: 4,
              },
            }}
          >
            Relatórios
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={handleExportReports}
            startIcon={
              isLoadingReport ? <CircularProgress size={20} /> : <Download />
            }
            disabled={isLoadingReport}
            sx={{
              padding: '8px 16px',
              textTransform: 'none',
              fontWeight: 500,
              boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
            }}
          >
            {isLoadingReport ? 'Exportando...' : 'Exportar Relatórios'}
          </Button>
        </CardHeader>
        <TableContainer>
          <Table
            stickyHeader
            sx={{
              mt: 3,
              '& .MuiTableCell-root': {
                padding: '16px 12px',
              },
            }}
          >
            <TableHead>
              <TableRow
                sx={{
                  th: {
                    backgroundColor: 'white',
                    '& p': {
                      fontWeight: 'bold',
                      color: theme.palette.primary.main,
                    },
                    '& div': {
                      display: 'flex',
                      alignItems: 'center',
                      '& button': { ml: 1 },
                    },
                  },
                }}
              >
                <TableCell>
                  <Typography>#</Typography>
                </TableCell>
                <TableCell align="left">
                  <Typography>Id</Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography>Data</Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography>Status</Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography>Sucessos</Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography>Falhas</Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography>Progresso</Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography>Tempo</Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography>Detalhes</Typography>
                </TableCell>
                <TableCell align="center"></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    <CircularProgress size={15} />
                    <Typography
                      paddingLeft={1}
                      variant="caption"
                      color="text"
                      sx={{ fontStyle: 'italic' }}
                    >
                      Carregando lista de relatórios...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : !reports.length ? (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    <Typography
                      paddingLeft={1}
                      variant="caption"
                      color="text"
                      sx={{ fontStyle: 'italic' }}
                    >
                      Nenhum relatório encontrado...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                reports.map((report: IFontTestViewModel, idx) => (
                  <TableRow
                    data-testid="rowReport"
                    key={report.id}
                    sx={{
                      '&:last-child td, &:last-child th': { border: 0 },
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      },
                    }}
                  >
                    <TableCell align="left">
                      <Typography color="primary" sx={{ fontWeight: 500 }}>
                        {(idx + 1).toString().padStart(3, '0')}
                      </Typography>
                    </TableCell>
                    <TableCell align="left">
                      <Typography>{report.id}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography>
                        {moment(report.createdAt).format('DD/MM/YYYY HH:mm')}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={statusLabel(report.status)}
                        variant="outlined"
                        color={
                          statusLabel(report.status) === 'Pendente'
                            ? 'warning'
                            : statusLabel(report.status) === 'Processando'
                            ? 'info'
                            : statusLabel(report.status) === 'Finalizado'
                            ? 'success'
                            : statusLabel(report.status) === 'Falha'
                            ? 'error'
                            : statusLabel(report.status) === 'Cancelado'
                            ? 'default'
                            : 'error'
                        }
                        sx={{
                          borderRadius: '16px',
                          fontWeight: 500,
                          padding: '4px 8px',
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Typography sx={{ color: '#167a12', fontWeight: 500 }}>
                        {
                          report.fonts?.filter(
                            (font: IFontReportViewModel) =>
                              font.status === FontStatus.AVAILABLE
                          ).length
                        }
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography sx={{ color: '#af1a3a', fontWeight: 500 }}>
                        {
                          report.fonts?.filter(
                            (font: IFontReportViewModel) =>
                              font.status === FontStatus.UNAVAILABLE
                          ).length
                        }
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {report.status === FontTestStatus.PROCESSING ? (
                        <Typography sx={{ fontWeight: 500 }}>
                          {report.progress.toFixed(1)}%
                        </Typography>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Typography>
                        {formatExecutionTime(report.executionTimeInSeconds)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        disabled={
                          report.status === FontTestStatus.PENDING ||
                          report.status === FontTestStatus.FAILED
                        }
                        aria-label="expand row"
                        size="small"
                        onClick={() => {
                          setCurrentReport(report);
                          setShowSummaryTest(true);
                        }}
                      >
                        <Visibility />
                      </IconButton>
                    </TableCell>
                    <TableCell align="center">
                      <Popconfirm
                        title="Cancelar Relatório"
                        description="Você tem certeza que deseja cancelar o Relatório?"
                        onConfirm={() => handleCancelReport(report.id)}
                        okText="Sim"
                        cancelText="Não"
                        placement="bottomRight"
                        disabled={
                          report.status !== FontTestStatus.PENDING &&
                          report.status !== FontTestStatus.PROCESSING
                        }
                      >
                        <IconButton>
                          <CloseOutlinedIcon
                            color={
                              report.status !== FontTestStatus.PENDING &&
                              report.status !== FontTestStatus.PROCESSING
                                ? 'disabled'
                                : 'error'
                            }
                          />
                        </IconButton>
                      </Popconfirm>
                    </TableCell>
                  </TableRow>
                ))
              )}
              {hasNextPage && (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    {isFetchingNextPage ? (
                      <Grid
                        item
                        alignItems="center"
                        sx={{ minHeight: '65px !important' }}
                      >
                        <CircularProgress size={15} />
                        <Typography
                          paddingLeft={1}
                          variant="caption"
                          color="text"
                          sx={{ fontStyle: 'italic' }}
                        >
                          Carregando itens...
                        </Typography>
                      </Grid>
                    ) : (
                      <Button
                        data-testid="buttonNextPage"
                        onClick={() => handleFetchNextPage()}
                        sx={{
                          px: 2,
                          my: 1,
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                          },
                        }}
                      >
                        <KeyboardDoubleArrowDown />
                        <Typography paddingLeft={2} variant="button">
                          Carregar mais...
                        </Typography>
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      {showSummaryTest && currentReport && (
        <Modals.SummaryTestModal
          onClose={() => setShowSummaryTest(false)}
          open={showSummaryTest}
          report={currentReport}
        />
      )}
      {openModalDetais && selectedFont && (
        <Modals.FontStatusDetailsModal
          onClose={() => setOpenModalDetais(false)}
          open={openModalDetais}
          history={selectedFont}
        />
      )}
    </>
  );
};

export default FontReportsTab;
